import {
  FC,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { safelyFetchDocument } from 'src/components/DocumenManagerV2/utils';
import ZoomControls from '../shared/ZoomControls';
import PdfPageRenderer from '../Signatures/PdfPageRenderer';
import type { HubFile } from './HubFileTile';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/static/pdf.worker.min.mjs';

const PREVIEW_ERROR_MESSAGE =
  "We couldn't load this document. Please try again.";

/**
 * Renter uploads are not always PDFs: the NRE pipeline can persist the raw
 * image (PNG/JPEG/…) into the document URL. pdfjs cannot parse those, so render
 * images natively instead. Raster-only allowlist, matching DocumentPreviewBox.
 *
 * Detection is driven by the URL — the bytes actually being fetched — because a
 * converted upload keeps its original image filename even though
 * `pdf_file_url` points at the generated PDF. The name is only a fallback for
 * URLs without an extension.
 */
const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'heic',
  'heif',
  'tiff',
  'tif',
  'bmp',
]);

function getExtension(value: string): string {
  const path = value.split(/[?#]/)[0];
  const dot = path.lastIndexOf('.');
  return dot === -1 ? '' : path.slice(dot + 1).toLowerCase();
}

function isImageDocument(url: string, name: string): boolean {
  const urlExtension = getExtension(url);
  if (urlExtension) {
    return IMAGE_EXTENSIONS.has(urlExtension);
  }
  return IMAGE_EXTENSIONS.has(getExtension(name));
}

interface FilePreviewModalProps {
  file: HubFile | null;
  onClose: () => void;
  /**
   * Optional action bar pinned under the document.
   *
   * A preview is where a compliance user decides what to do with a renter
   * upload; making them close it to reach the tile's buttons is a round trip
   * for nothing. Callers that only want a viewer (the renter hub) pass nothing.
   *
   * The bar is a sibling of the scroll region, never an overlay: the buttons
   * keep their own strip at any zoom, and no floating chrome shares that
   * corner (zoom lives in the header).
   */
  actions?: ReactNode;
  /**
   * Optional one-line context shown at the leading edge of the action bar
   * (e.g. "Held for review"). Ignored when `actions` is absent — a note with
   * no decision to make is noise.
   */
  footerNote?: ReactNode;
}

const FilePreviewModal: FC<FilePreviewModalProps> = ({
  file,
  onClose,
  actions = null,
  footerNote = null,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [pages, setPages] = useState<PDFPageProxy[]>([]);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageWidth, setImageWidth] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);

  const fileUrl = file?.pdf_file_url ?? null;

  useEffect(() => {
    setZoom(1);
    setPages([]);
    setImageSrc(null);
    setImageWidth(0);
    if (!fileUrl) {
      setLoading(false);
      setError(file ? PREVIEW_ERROR_MESSAGE : null);
      return;
    }
    setLoading(true);
    setError(null);

    // Images render straight from the S3 URL (no blob), so nothing to fetch and
    // no same-origin blob URL to navigate to. Loading/error resolve via the
    // <img> load handlers below.
    if (isImageDocument(fileUrl, file?.name ?? '')) {
      setImageSrc(fileUrl);
      return;
    }

    let cancelled = false;

    async function load(): Promise<void> {
      try {
        const blob = await safelyFetchDocument(fileUrl as string, 3);
        const arrayBuffer = await blob.arrayBuffer();
        const pdfDoc = await pdfjsLib.getDocument({
          data: arrayBuffer,
          wasmUrl: '/static/wasm/',
        }).promise;
        if (cancelled) {
          pdfDoc.cleanup();
          return;
        }
        pdfDocRef.current = pdfDoc;
        const loadedPages: PDFPageProxy[] = [];
        for (let i = 1; i <= pdfDoc.numPages; i += 1) {
          loadedPages.push(await pdfDoc.getPage(i));
        }
        if (!cancelled) {
          setPages(loadedPages);
        }
      } catch {
        if (!cancelled) {
          setError(PREVIEW_ERROR_MESSAGE);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();

    return () => {
      cancelled = true;
      if (pdfDocRef.current) {
        pdfDocRef.current.cleanup();
        pdfDocRef.current = null;
      }
    };
  }, [file, fileUrl, retryKey]);

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>): void => {
    setImageWidth(event.currentTarget.naturalWidth || 0);
    setLoading(false);
  };

  const handleImageError = (): void => {
    setError(PREVIEW_ERROR_MESSAGE);
    setLoading(false);
  };

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, [pages.length, imageSrc]);

  const firstPageWidth =
    pages.length > 0 ? pages[0].getViewport({ scale: 1 }).width : 0;
  const contentWidth = pages.length > 0 ? firstPageWidth : imageWidth;
  const fitScale =
    contentWidth > 0 && containerWidth > 0
      ? Math.min(containerWidth / contentWidth, 2)
      : 1;
  const scale = fitScale * zoom;

  const showZoomControls =
    !loading && !error && (pages.length > 0 || !!imageSrc);

  return (
    <Dialog
      open={file != null}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, gap: 1, flexShrink: 0 }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 600, minWidth: 0 }} noWrap>
          {file?.name ?? ''}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ flexShrink: 0 }}
        >
          {showZoomControls && (
            <ZoomControls zoom={zoom} onChange={setZoom} inline />
          )}
          <IconButton onClick={onClose} aria-label="Close preview" size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Stack>
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'auto',
            bgcolor: 'grey.200',
            p: 1,
          }}
        >
          {loading && (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={28} />
            </Stack>
          )}
          {!loading && error && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 3,
              }}
            >
              <Card role="alert" sx={{ width: '100%', maxWidth: 400 }}>
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Unable to preview this file
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {PREVIEW_ERROR_MESSAGE}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setRetryKey((k) => k + 1)}
                  >
                    Retry
                  </Button>
                </CardContent>
              </Card>
            </Box>
          )}
          {!loading &&
            !error &&
            pages.map((page, index) => (
              <PdfPageRenderer
                key={`page-${index}`}
                page={page}
                scale={scale}
                pageIndex={index}
              />
            ))}
          {imageSrc && (
            <Box
              key={retryKey}
              component="img"
              src={imageSrc}
              alt={file?.name ?? 'Document preview'}
              onLoad={handleImageLoad}
              onError={handleImageError}
              sx={{
                display: loading || error ? 'none' : 'block',
                mx: 'auto',
                mb: 0.5,
                width:
                  imageWidth > 0
                    ? `${Math.floor(imageWidth * scale)}px`
                    : '100%',
                maxWidth: 'none',
                height: 'auto',
                boxShadow: 1,
                backgroundColor: '#fff',
              }}
            />
          )}
        </Box>
      </Box>
      {actions && (
        <Box
          data-testid="file-preview-actions"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
        >
          {footerNote ? (
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>{footerNote}</Box>
          ) : null}
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ ml: 'auto', flexShrink: 0 }}
          >
            {actions}
          </Stack>
        </Box>
      )}
    </Dialog>
  );
};

export default FilePreviewModal;
